import {
	PickOrderLine,
	PickedQuantities,
	activeLines,
	byOldestFulfillmentOrder,
	pickedFor,
} from './draft'
import { ProductIdentity, productIdentity } from './productIdentity'

/** One fulfillment order's claim on a product the picker is collecting. */
export type PickItemLine = {
	line: PickOrderLine,
	/** What this order asked for. */
	toPick: number,
	/** What the current found count gives this order. */
	picked: number,
}

/**
 * A product as the picker handles it: one entry per product, however many
 * fulfillment orders are waiting on it.
 */
export type PickItem = {
	/** `Part.id` — a number, not an `ID`: this product comes from the REST schema. */
	productId: PickOrderLine['product']['id'],
	identity: ProductIdentity,
	/** Every line for this product, oldest fulfillment order first. */
	lines: PickItemLine[],
	/** Where to find it. One bin today: a product has one `StorePart` per DC. */
	binLocation: string | null,
	/** Called for, across every order in the run. */
	toPick: number,
	/** Found so far, across every order in the run. */
	picked: number,
}

/**
 * Bin order — the order the products are walked in.
 *
 * Bin codes are alphanumeric and assigned by hand, so they sort naturally
 * rather than lexically: `A-2` belongs before `A-10`, which a plain string
 * compare gets backwards. `sensitivity: 'base'` keeps a code someone entered in
 * lower case with its neighbours instead of after every upper-case one.
 *
 * Two products in one bin are broken apart by SKU, so the list reads the same way
 * every time it is built rather than inheriting whatever order the lines
 * arrived in.
 *
 * A product with no bin sorts last. There is no shelf to walk to, so it is not
 * part of the route — the picker has to look it up, and that is better done
 * once at the end than halfway down an aisle.
 */
export function byBinLocation( a: PickItem, b: PickItem ): number {
	if ( a.binLocation === null || b.binLocation === null ) {
		const placement = Number( a.binLocation === null ) - Number( b.binLocation === null )
		if ( placement !== 0 ) {
			return placement
		}
	} else {
		const byBin = a.binLocation.localeCompare( b.binLocation, undefined, { numeric: true, sensitivity: 'base' } )
		if ( byBin !== 0 ) {
			return byBin
		}
	}
	return ( a.identity.sku ?? '' ).localeCompare(
		b.identity.sku ?? '',
		undefined,
		{ numeric: true, sensitivity: 'base' },
	)
}

/**
 * What the Pick step works from, in the order the products are walked.
 *
 * Pick order lines are one per fulfillment order line item, so two orders
 * wanting the same product arrive as two lines — and a picker asked to count the
 * same bin twice will reasonably wonder whether they have already done it. The
 * floor holds products, not orders, so the lines are grouped by product here and
 * the split back out to orders is arithmetic the app does.
 *
 * The grouping is the same one the Pack step uses. The bin ordering is not:
 * packing happens at a bench, where where-it-came-from no longer matters.
 */
export function pickItems( lines: PickOrderLine[], picked: PickedQuantities ): PickItem[] {
	const byProduct = new Map<PickOrderLine['product']['id'], PickOrderLine[]>()

	for ( const line of activeLines( lines ) ) {
		const entries = byProduct.get( line.product.id ) ?? []
		entries.push( line )
		byProduct.set( line.product.id, entries )
	}

	const items = [ ...byProduct.entries() ].map( ( [ productId, entries ] ) => {
		const ordered = [ ...entries ].sort( byOldestFulfillmentOrder )
		return {
			productId,
			identity: productIdentity( ordered[ 0 ].product ),
			lines: ordered.map( ( line ) => ( {
				line,
				toPick: line.quantityToPick,
				picked: pickedFor( picked, line.id ),
			} ) ),
			// Every line for a product shares a bin, because the bin is copied from
			// the product's StorePart at the DC. Reading it off the first line rather
			// than asserting they agree keeps that assumption where a reader will
			// meet it if multi-bin ever makes it false.
			binLocation: ordered[ 0 ].binLocation ?? null,
			toPick: ordered.reduce( ( sum, line ) => sum + line.quantityToPick, 0 ),
			picked: ordered.reduce( ( sum, line ) => sum + pickedFor( picked, line.id ), 0 ),
		}
	} )

	return items.sort( byBinLocation )
}

/**
 * Record how many of a product the picker found, and settle which orders get
 * them — oldest fulfillment order first.
 *
 * A picker counts what is in their hand; they are not holding one order's six
 * and another's four as separate piles, and asking them to decide which order
 * goes short is a question about paperwork, not about the shelf. So the count
 * is one number per product and the allocation is the app's.
 *
 * Each order is filled before the next is started, so a short pick leaves the
 * newest order holding the whole gap rather than shorting every order a little.
 * One order then ships complete. Packing carries the same rule forward, so an
 * order filled here is the order boxed first.
 *
 * Returns absolute picked counts for every line the product covers.
 */
export function allocatePickedQuantity( item: PickItem, found: number ): PickedQuantities {
	const allocated: PickedQuantities = {}
	let left = Math.min( Math.max( found, 0 ), item.toPick )

	for ( const entry of item.lines ) {
		const take = Math.min( entry.toPick, left )
		allocated[ entry.line.id ] = take
		left -= take
	}

	return allocated
}

/** How much of a product is still to find. */
export function shortfallForItem( item: PickItem ): number {
	return Math.max( item.toPick - item.picked, 0 )
}

/**
 * Which orders a count would fill, e.g. `FO-165 ×2 · FO-171 ×4`.
 *
 * Takes the count rather than reading `item.picked`, so the card can show the
 * split for a number the picker has typed but not yet confirmed. The picker
 * never chooses the split, but a short count means somebody's order is leaving
 * incomplete, and that is worth seeing before it is recorded rather than after.
 */
export function pickOrderBreakdown( item: PickItem, found: number ): string {
	const allocated = allocatePickedQuantity( item, found )
	return item.lines
		.filter( ( entry ) => ( allocated[ entry.line.id ] ?? 0 ) > 0 )
		.map( ( entry ) => `FO-${entry.line.fulfillmentOrder.orderNumber} ×${allocated[ entry.line.id ]}` )
		.join( ' · ' )
}

/** Where a count would leave units owed, when it comes up short. */
export function shortfallBreakdown( item: PickItem, found: number ): string {
	const allocated = allocatePickedQuantity( item, found )
	return item.lines
		.filter( ( entry ) => entry.toPick > ( allocated[ entry.line.id ] ?? 0 ) )
		.map( ( entry ) => `FO-${entry.line.fulfillmentOrder.orderNumber} ×${entry.toPick - ( allocated[ entry.line.id ] ?? 0 )}` )
		.join( ' · ' )
}

/** What a product is called by, for the run's own bookkeeping. */
export type ProductId = PickItem['productId']

/**
 * What the picker has typed on the Pick step, per product. A product with no
 * entry is blank: nothing typed yet, which is not the same as a typed zero
 * while they are still working. Both mean zero once they continue to pack.
 */
export type PickEntries = Partial<Record<ProductId, number>>

/**
 * Where the Pick step starts.
 *
 * Blank, unless counts have been saved before — continuing to pack is what
 * saves them, and inventory-api cannot tell an untouched line from a zero, so
 * once a run has saved counts they come back as numbers, zeros included.
 */
export function initialPickEntries( items: PickItem[], hasSavedCounts: boolean ): PickEntries {
	if ( !hasSavedCounts ) {
		return {}
	}
	return Object.fromEntries( items.map( ( item ) => [ item.productId, item.picked ] ) )
}

/** Absolute picked counts for every line, with a blank read as zero. */
export function pickedFromEntries( items: PickItem[], entries: PickEntries ): PickedQuantities {
	return items.reduce<PickedQuantities>(
		( acc, item ) => ( { ...acc, ...allocatePickedQuantity( item, entries[ item.productId ] ?? 0 ) } ),
		{},
	)
}

/** Products the picker left blank. */
export function unenteredItems( items: PickItem[], entries: PickEntries ): PickItem[] {
	return items.filter( ( item ) => entries[ item.productId ] === undefined )
}
